import {
  type ConductorTask,
  createSimpleTask,
  getTaskReferenceName,
  wrapVariables
} from 'Automations/nodeDefinitions/conductor'
import type {
  ConditionSupportingTask,
  ConditionValueSupportingTask
} from 'Automations/nodeDefinitions/conditions'
import {
  readQuoteTemplateValue,
  scopedSemanticTypeKey
} from 'Automations/nodeDefinitions/conditions/helpers/functionStrings'
import {
  type SemanticType,
  semanticTypes
} from 'Automations/nodeDefinitions/semanticTypes'
import {
  isQuoteComparisonField,
  type QuoteComparisonField
} from '../quoteComparison'
import { TASK_EXECUTORS } from 'Automations/automations.constants'

const taskReferenceBuilder = getTaskReferenceName<string>('SWITCH')

/**
 * Fields whose value comes straight off the template fetch: the scoped semantic
 * type it is published under, and the executor output that supplies it. Pricing
 * is absent because an executor compares it — see `pricingComparisonBranch`.
 */
const TEMPLATE_FIELD_BY_OPERATOR: Record<
  Exclude<QuoteComparisonField, 'PRICING'>,
  { semanticType: SemanticType; outputField: string }
> = {
  ATTACHMENTS: {
    semanticType: semanticTypes.sequence.quoteTemplate.attachments,
    outputField: 'attachmentAssets'
  },
  RAW_TEXT: {
    semanticType: semanticTypes.sequence.quoteTemplate.rawText,
    outputField: 'rawText'
  },
  PAYMENT_TERMS: {
    semanticType: semanticTypes.sequence.quoteTemplate.paymentTerms,
    outputField: 'defaultDueDateDays'
  }
}

/**
 * Fetches the template a QUOTE_TEMPLATE condition compares against. The quote
 * approval trigger only carries a quote id, so the template has to be resolved
 * as part of the workflow.
 */
export const quoteTemplateSupportingTask: ConditionValueSupportingTask = ({
  conditionNodeId,
  operator,
  value,
  inputMapping
}) => {
  const { templateId } = readQuoteTemplateValue(value)
  // A half-configured row emits no task. Generation runs on every save and a
  // throw there is swallowed for an inert placeholder workflow; validation
  // surfaces the missing template or field in the editor instead.
  if (!templateId || !isQuoteComparisonField(operator)) return undefined

  // Named after the template, never the condition row, so two rows comparing
  // against the same template share one fetch — the condition node merges
  // branches on this name. Subject-derived also means it survives re-ordering,
  // keeping generated definitions diff-clean across versions.
  const taskReferenceName = taskReferenceBuilder(conditionNodeId)
  const fetchReference = taskReferenceName(`getQuoteTemplate_${templateId}`)

  const fetchTemplate = createSimpleTask({
    name: 'get_sequence_data',
    taskReferenceName: fetchReference,
    inputParameters: {
      taskExecutorConfiguration: {
        executor: TASK_EXECUTORS.quoteTemplate,
        version: 1
      },
      // A literal id, so no `wrapVariables` — nothing here references
      // another task's output.
      data: { quoteTemplateId: templateId }
    }
  })

  if (operator === 'PRICING') {
    return pricingComparisonBranch({
      taskReferenceName,
      fetchTemplate,
      fetchReference,
      templateId,
      quotePhases: inputMapping[semanticTypes.sequence.quote.phases]
    })
  }

  const field = TEMPLATE_FIELD_BY_OPERATOR[operator]

  return {
    tasks: [fetchTemplate],
    // Only the compared field is published, so the switch carries no input it
    // doesn't read. Rows on one template each add a key to the shared fetch.
    semanticTypeMapping: {
      [scopedSemanticTypeKey({
        semanticType: field.semanticType,
        scope: templateId
      })]: `${fetchReference}.output.quoteTemplate.${field.outputField}`
    }
  }
}

/**
 * Pricing is compared by the `compare_quote_pricing` executor, so the row needs
 * a second task reading the phases the fetch returned — one ordered branch
 * rather than two parallel fetches.
 *
 *   getQuoteTemplate_<id> -> compareQuotePricing_<id> -> switch reads a outcome
 */
const pricingComparisonBranch = ({
  taskReferenceName,
  fetchTemplate,
  fetchReference,
  templateId,
  quotePhases
}: {
  taskReferenceName: (name: string) => string
  fetchTemplate: ConductorTask
  fetchReference: string
  templateId: string
  quotePhases: string | undefined
}): ConditionSupportingTask | undefined => {
  // Without the quote's phases there is nothing to compare against, so emit no
  // task rather than one holding an input that will never resolve.
  if (!quotePhases) return undefined

  const compareReference = taskReferenceName(
    `compareQuotePricing_${templateId}`
  )

  return {
    tasks: [
      fetchTemplate,
      createSimpleTask({
        name: 'get_sequence_data',
        taskReferenceName: compareReference,
        inputParameters: {
          taskExecutorConfiguration: {
            executor: TASK_EXECUTORS.compareQuotePricing,
            version: 1
          },
          // `source` is the quote under review, `target` the template it is
          // measured against — the order the executor reports differences in.
          data: wrapVariables({
            source: quotePhases,
            target: `${fetchReference}.output.quoteTemplate.phases`
          })
        }
      })
    ],
    // One comparison answers every pricing question, so publish each outcome
    // rather than branching on this row's comparison — rows on one template share
    // the branch, and `pruneUnusedInputs` drops unreferenced keys.
    semanticTypeMapping: Object.entries(
      semanticTypes.sequence.quotePricingComparison
    ).reduce<Record<string, string>>(
      (mapping, [outcome, semanticType]) => ({
        ...mapping,
        // The executor's output is flat — `valueToTree` with no wrapper key,
        // unlike the get-quote executor's `output.quote`.
        [scopedSemanticTypeKey({
          semanticType,
          scope: templateId
        })]: `${compareReference}.output.${outcome}`
      }),
      {}
    )
  }
}
